import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Tooltip } from 'antd';
import useDateFormat from '../../hooks/useDateFormat';
import useTimeFormat from '../../hooks/useTimeFormat';
import useUnitSystem from '../../hooks/useUnitSystem';

import MarkdownContent from '../common/MarkdownContent';
import MeasurementValue from '../common/MeasurementConversions';
import ValueWithAction, { hasValueAction } from '../common/ValueAction';
import MaskedValue from '../common/MaskedValue';
import { isMaskedElement } from '../../utils/maskedElements';
import LookupRefs from '../common/LookupRefs';
import { RecordTitle } from '../common/RecordBadge';
import { primaryDateForChain, resolveItemPrimaryDate } from '../../utils/cards';
import { formatDateRange } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { CHEVRON_DOWN, CHEVRON_UP } from '../icons';
import getItemDisplayValue from '../../utils/items';
import ItemActionsMenu from './ItemActionsMenu';
import ItemAuditLine from './ItemAuditLine';
import ItemOpenButton from './ItemOpenButton';
import IntakePendingTag from './IntakePendingTag';
import DemoTag from './DemoTag';
import CopiedFromNote from './CopiedFromNote';
import ItemTagChips from '../tags/ItemTagChips';
import { rememberedItemTagTitles } from '../../hooks/useTagSearch';
import { itemTagEntries, publicTagEntries } from '../../utils/itemTags';
import { tileShowsTags } from '../cards/tileLayoutModel';
import { isLongType, readTileBody } from '../../config/tileSlotPolicy';
import { markdownToPlainText } from '../../utils/markdown';
import {
	elementLabel,
	formatPrimaryDate,
	formatValue,
	getChoiceMeta,
	resolveTileLayout,
	summarizeMultiDate,
} from './itemValueDisplay';
import UserValue from '../users/UserValue';
import { isEmptyUserValue as isEmptyUser } from '../../utils/userElements';
import { LINKED_ROW_EXCLUDED_ACTIONS, isLinkedRow, rowSourceOf } from '../../utils/linkedRecords';
import { rowSourceIdentity } from '../../utils/recordQualifier';
import ChoiceChips from './ChoiceChips';
import { isChoiceList } from '../../utils/choiceValues';
import { FileTileValue, tileShowsPicture } from '../common/FileValue';
import { fileElementVisible, filesOf, isFileElement } from '../../utils/fileValues';
import { isConfirmationsElement, readCount } from '../../utils/confirmationValues';

/**
 * One structured field on a uniform card: micro-label over value. Every
 * value reads at the same size and weight whatever its data type; enums
 * bring their choice icon/color along.
 */
const CardField = ({ element, value, endValue, resolveLookupValue, unitSystem, collectionId, span = null, item = null, dense = false }) => {
	const choiceMeta = getChoiceMeta(element, value);
	// A file element (docs/plans/data-types-file-element.md §6, F9): a photo
	// is a 72 px cover-cropped `sm` with its label hidden; anything else the
	// dense chip; a list its first file; an id the read has no facts for,
	// nothing.
	const isFile = element?.dataType === 'file';
	const picture = isFile && !dense && tileShowsPicture(item, value);
	// Several dates in one 162px slot: the EARLIEST, then how many more —
	// "Sep 9 +2" — with the whole list in the tooltip. A tile says what a
	// record is, not everything it holds; the read view lists every one.
	const multiDate = summarizeMultiDate(element, value);
	let display;
	if (isMaskedElement(element)) {
		// A masked element (KPR-1348): its mask, with Show and Copy — on the
		// compact tile too, which is the one action it keeps: a tile with a
		// code on it is a tile someone came to read the code from.
		display = <MaskedValue element={element} value={value} prefs={{ unitSystem }} resetKey={item?._id} />;
	} else if (isFile) {
		display = filesOf(item, value).length ? <FileTileValue item={item} value={value} dense={dense} /> : '—';
	} else if (isLongType(element?.dataType)) {
		// Long text placed beside other fields (an arranged body): its first
		// words on one line, ellipsized. Alone across the tile it is the
		// paragraph instead, which never reaches here (resolveTileLayout).
		const text = element.dataType === 'rich-text'
			? markdownToPlainText((value || '').toString())
			: (value || '').toString().replace(/\s+/g, ' ').trim();
		display = text || '—';
	} else if (multiDate) {
		display = (
			<Tooltip title={multiDate.all}>
				<span data-testid="multi-date-summary">
					{multiDate.lead}
					<span className="item-card__field-more"> +{multiDate.extra}</span>
				</span>
			</Tooltip>
		);
	} else if (element?.rangeEndDef) {
		// A span is one field: "Sep 9 – Sep 12 · 3 days" (pickDisplayFields
		// pairs it and drops the end element's own slot).
		display = formatDateRange(value, endValue, element?.dataType) || '—';
	} else if (isConfirmationsElement(element)) {
		// A "Me too" count (KPR-390): the number, never a 0 — nobody yet reads
		// as any empty field does. A tile is a click target, so the toggle
		// waits in the read view.
		display = readCount(value) > 0 ? formatValue(element, value) : '—';
	} else if (element?.dataType === 'user') {
		// The person, or their record in this collection, at tile size.
		display = isEmptyUser(value) ? '—' : <UserValue element={element} value={value} collectionId={collectionId} size="xs" />;
	} else if (element?.dataType === 'card-lookup') {
		// The tile drops the conversions ⇄ and the Call / Open triggers for
		// want of room, but a reference needs none: the title it already
		// shows IS the door to every record pointing at the same item.
		// A reference into another collection is its qualified badge.
		display = <LookupRefs element={element} value={value} resolveLookupValue={resolveLookupValue} size="xs" />;
	} else if (element?.dataType === 'measurement') {
		// Formatted text only — the compact card has no room for the
		// conversions trigger; it waits in the detail, the read view, the
		// table and the form.
		display = <MeasurementValue element={element} value={value} unitSystem={unitSystem} conversions={false} />;
	} else if (isChoiceList(element, value)) {
		// A Choice that allows multiple values: its chips — the first two,
		// then "+N" with every label in the tooltip (a tile says what a record
		// is; the read view lists every one).
		display = <ChoiceChips element={element} value={value} max={2} />;
	} else if (hasValueAction(element?.dataType)) {
		// Formatted text only: the compact tile drops the Call / Compose /
		// Open trigger exactly as it drops the conversions ⇄ — a 162px card
		// has no room, and the trigger waits in the detail, the table and the
		// read view.
		display = <ValueWithAction element={element} value={value} action={false} />;
	} else {
		display = choiceMeta.label || formatValue(element, value, { unitSystem }) || '—';
	}
	const ChoiceIcon = choiceMeta.icon;
	return (
		// An arranged tile gives each field a share of the row's three columns;
		// an automatic one lets them divide it evenly, as they always have.
		<div
			className={`item-card__field${picture ? ' item-card__field--picture' : ''}`}
			style={span ? { '--field-span': span } : undefined}
		>
			{picture ? null : <div className="micro-label item-card__field-label">{elementLabel(element)}</div>}
			<div className="item-card__field-value">
				{choiceMeta.color ? (
					<span className="item-element__value-dot" style={{ backgroundColor: choiceMeta.color }} />
				) : null}
				{ChoiceIcon ? <span className="item-element__value-icon"><ChoiceIcon /></span> : null}
				<span className="item-card__field-text">{display}</span>
			</div>
		</div>
	);
};

/**
 * The automatic pick, with a photo first (docs/plans/data-types-file-element.md
 * §6): with NO arrangement, the first image-only ("Photos only") file element
 * that holds a value this read describes leads the fields, and the usual pick
 * fills the remaining columns — it displaces the last automatically picked
 * field. An arranged tile is the author's answer and is left alone, as is a
 * text-led tile (its paragraph is the body). A body not in sixths is no
 * arrangement (tileSlotPolicy readTileBody).
 */
export const photoFirst = (display, tileLayout, elementDefs, item) => {
	if (Array.isArray(readTileBody(tileLayout?.body)?.rows) || display?.mode !== 'fields') return display;
	const lead = (elementDefs || []).find(def => def?.options?.accept === 'image' && isFileElement(def)
		&& filesOf(item, item?.elements?.[def.name]).length > 0);
	if (!lead) return display;
	// Every non-paragraph field is picked up to the limit, so a lead left out
	// means the tile was full: it takes the first column and the last falls off.
	const rest = display.fields.filter(def => def?.name !== lead.name);
	return { ...display, fields: [lead, ...rest].slice(0, Math.max(display.fields.length, 1)) };
};

/**
 * The uniform item card: a fixed-height tile with the item's two-line badge
 * on top (the card's tile; the card name, muted, over the item's title),
 * then either a 3-line-clamped text body or up to three structured fields,
 * and the created/updated audit line (ItemAuditLine) as the footer — the
 * only place the tile says when. A card whose primary date is one of its
 * own date elements shows that date at the header's right; the created or
 * updated timestamp never repeats up there.
 *
 * Every item action is on the card through its ⋯ menu, with the Open door
 * promoted to its own button beside it and the selection checkbox after
 * them. On a pointer device all three take no room at rest and arrive on
 * hover or focus (the title gives way); on touch Open and ⋯ always show.
 * The checkbox shows on every tile once anything is checked
 * (`selectionActive`). Selection opens the detail — the pane on
 * desktop, or, when `expanded` with a `renderExpanded`, the card itself
 * grows into the detail in place (phones). With `renderExpanded` the
 * header also carries an always-visible chevron that toggles that
 * expansion (down = show, up = hide) beside the ⋯ menu.
 *
 * MEMOISED (KPR-1557, R3.1): a list of hundreds of tiles re-renders only the
 * tiles whose props changed, by React.memo's own shallow compare, so every
 * prop a list passes must keep its identity between renders (ItemCardsGrid
 * makes them so). What a tile used to learn only from its parent
 * re-rendering it, it now asks for itself: the date and time formats.
 */
const ItemCardTile = ({
	item,
	card,
	elementDefs,
	resolveLookupValue,
	selected = false,
	onSelect,
	readOnly = false,
	expanded = false,
	renderExpanded,
	collection = null,
	collectionId,
	canEdit = true,
	// Manage on the record's collection (Unlock…), and the card cache the
	// record policy is read through (KPR-155).
	canManage: canManageRecord = false,
	cardsById = null,
	onDeleted,
	onClose,
	// Measurement display: null = the viewer's preference (the grid passes
	// the public page's 'default' through).
	unitSystem: unitSystemOverride = null,
	// The card's primary-date spec (utils/cards.js), resolved through the
	// parent chain by the grid. A card rendered alone (the form's "last
	// item") reads its own setting against the merged defs it was given.
	primaryDate = null,
	// The stored arrangement for this card, already cascaded (card ->
	// collection -> user) by the API. Null — a card nobody has arranged, a
	// public share, the form's "last item" — means the automatic pick.
	tileLayout = null,
	// ---- multi-selection (owned by the page; see hooks/useItemSelection) ----
	// Null when the surface has no selection at all. `checked` is this tile's
	// state; `onToggleCheck(id, { shiftKey })` reports a change; `checkable`
	// false means this row cannot take part (a closed record, a card the
	// reader cannot write) and the checkbox says why instead of appearing.
	selectable = false,
	checked = false,
	checkable = true,
	checkDisabledReason = null,
	onToggleCheck,
	// Touch: a long press enters selection mode and takes this tile with it.
	onLongPress,
	selectionMode = false,
	// Something in the list is checked (or a phone is in selection mode):
	// every tile shows its checkbox, not only the hovered one.
	selectionActive = false,
	// The names the tile's tags read in: `{ vocabulary, privateTags }` —
	// the grid asks once for every tile. A tile alone (the form's last
	// item) reads the item's own tagTitles.
	tagContext = null,
	// The item as it stands after ⋯ Tag… (the list's feed).
	onItemUpdated,
	// On a surface that belongs to no collection (the tag page), the
	// collection this tile's title is qualified with — its own.
	ownCollection = null,
	// The ⋯ to draw instead of the item's own menu, given the same props
	// (the board's, KPR-1395, which adds Move to…); `actionsMenuProps` are
	// added to them.
	actionsMenu: ActionsMenu = ItemActionsMenu,
	actionsMenuProps = null,
}) => {
	useDateFormat(); // re-render when the date-format preference changes
	useTimeFormat(); // and the time format (an element's time, the audit line)
	const preferredUnitSystem = useUnitSystem();
	const unitSystem = unitSystemOverride || preferredUnitSystem;
	// Identity color reaches exactly two places: the badge tile and the 3px
	// left edge the CSS derives from this variable.
	const accentStyle = card?.color ? { '--card-accent': card.color } : undefined;
	const title = getItemDisplayValue({ card, elementDefs, item, resolveLookupValue });
	const cardType = card?.label?.singular || card?.name || 'Item';
	// Where the record lives when that is not this list's collection (a
	// linked row, a sub-collection's record in its parent's family list, a
	// parent's record read by reference): its title is the qualified pill.
	const rowSource = rowSourceOf(item, collection);

	// A primary date that is one of the card's OWN date elements leads the
	// header, readable: "Sep 9" for a date element, "Sep 9, 3:15 PM" with a
	// time; the tooltip says which date it is. Under an ordered spec it is
	// whichever entry THIS item has a value for ("Due" when Scheduled is
	// blank). Created and updated are the audit line's at the foot and are
	// never repeated up here — an item that fell through to its terminal
	// shows no header date, as a blank single date shows none.
	const spec = useMemo(
		() => primaryDate || primaryDateForChain([card], elementDefs),
		[primaryDate, card, elementDefs]
	);
	const resolved = useMemo(() => resolveItemPrimaryDate(spec, item), [spec, item]);
	const headerDate = resolved.kind === 'element' ? formatPrimaryDate(resolved, resolved.value, { short: true }) : '';
	const headerDateFull = resolved.kind === 'element' ? formatPrimaryDate(resolved, resolved.value) : '';

	// The primary-date element is in the header already, so it leaves the
	// body — under an ordered spec, the one that supplied this tile's header.
	const excluded = resolved.kind === 'element' ? resolved.element?.name || null : (spec.element?.name || null);
	// A stored layout (cardLayouts, cascaded card -> collection -> user) is the
	// whole answer when there is one; with none, the automatic pick every card
	// has always had.
	//
	// A file element shows only while the reader passes the attachments gate
	// (utils/fileValues fileElementVisible); with no arrangement, the first
	// "Photos only" file element holding a photo leads (photoFirst).
	const filesVisible = fileElementVisible({ item, collection });
	const tileDefs = useMemo(
		() => (filesVisible ? elementDefs : (elementDefs || []).filter(def => !isFileElement(def))),
		[elementDefs, filesVisible]
	);
	const display = useMemo(
		() => photoFirst(resolveTileLayout(tileLayout, tileDefs, { max: 3, exclude: excluded }), tileLayout, tileDefs, item),
		[tileLayout, tileDefs, excluded, item]
	);

	// TAGS SHOW ON A TILE BY DEFAULT (ruled d15): up to three badges and
	// "+N", on one line under the body, read-only. A tile layout may turn
	// them off (`body.tags: false`, the layout editor's Show tags). A tile
	// with none shows no row at all, so its body keeps the room.
	//
	// On the PUBLIC page (`tagContext.public`, T7) only what the page
	// discloses, and nothing for an id it names no tag for
	// (utils/itemTags.js publicTagEntries).
	const tagEntries = useMemo(() => {
		if (!tileShowsTags(tileLayout?.body)) return [];
		if (tagContext?.public) return publicTagEntries(item);
		return itemTagEntries(item, {
			vocabulary: tagContext?.vocabulary || null,
			privateTags: tagContext?.privateTags || null,
			titles: rememberedItemTagTitles(),
		});
	}, [item, tileLayout, tagContext]);

	// The expansion animates in two beats: the card switches to auto height
	// with the detail mounted at 0fr, then (next frame) the rows open.
	const [open, setOpen] = useState(false);
	useEffect(() => {
		if (!expanded) {
			setOpen(false);
			return undefined;
		}
		const frame = requestAnimationFrame(() => setOpen(true));
		return () => cancelAnimationFrame(frame);
	}, [expanded]);

	const interactive = !readOnly && !expanded && typeof onSelect === 'function';
	// The chevron is the card's own expand/collapse door (phones): always
	// visible, same slot in both states, so the header never shifts.
	const inlineExpandable = typeof renderExpanded === 'function';
	const ChevronIcon = expanded ? CHEVRON_UP : CHEVRON_DOWN;
	const toggleExpanded = () => {
		if (expanded) {
			if (typeof onClose === 'function') onClose();
		} else if (typeof onSelect === 'function') {
			onSelect(item);
		}
	};
	const id = normalizeId(item?._id);
	const toggleCheck = (event) => {
		if (!checkable) return;
		onToggleCheck?.(id, { selected: !checked, shiftKey: Boolean(event?.shiftKey) });
	};

	// A long press is the phone's way in (§5 Lists). 500ms, cancelled by a
	// move or an early lift, and it must not also fire the tap that follows.
	const pressRef = useRef({ timer: null, fired: false });
	const cancelPress = () => {
		if (pressRef.current.timer) clearTimeout(pressRef.current.timer);
		pressRef.current.timer = null;
	};
	const touchProps = selectable && typeof onLongPress === 'function' ? {
		onTouchStart: () => {
			pressRef.current.fired = false;
			cancelPress();
			pressRef.current.timer = setTimeout(() => {
				pressRef.current.fired = true;
				if (checkable) onLongPress(id);
			}, 500);
		},
		onTouchMove: cancelPress,
		onTouchEnd: cancelPress,
		onTouchCancel: cancelPress,
	} : {};

	const [titleTipFocus, setTitleTipFocus] = useState(false);
	const [titleTipHover, setTitleTipHover] = useState(false);
	// The qualifier chip is its own hover target with the relation tooltip
	// ("Shared from … read-only"): over it the title's tooltip stays shut, so a
	// linked tile never stacks two tooltips.
	const [overQualifier, setOverQualifier] = useState(false);

	const interactiveProps = interactive ? {
		onFocus: (event) => {
			if (event.target === event.currentTarget && event.target.matches?.(':focus-visible')) setTitleTipFocus(true);
		},
		onBlur: () => setTitleTipFocus(false),
		role: 'button',
		tabIndex: 0,
		onClick: (event) => {
			// A long press has already acted; the tap that ends it must not
			// also open the item.
			if (pressRef.current.fired) { pressRef.current.fired = false; return; }
			// Shift-click ranges, and in selection mode a plain tap picks
			// rather than opens — a phone has no modifier key to hold.
			if (selectable && (event?.shiftKey || selectionMode)) {
				event.preventDefault();
				toggleCheck(event);
				return;
			}
			onSelect(item);
		},
		onKeyDown: (event) => {
			// `x` toggles the focused tile, the same key the page registers.
			if (selectable && (event.key === 'x' || event.key === 'X')) {
				event.preventDefault();
				toggleCheck(event);
				return;
			}
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				onSelect(item);
			}
		},
		...touchProps,
	} : {};

	const classes = [
		'item-card',
		selected ? 'item-card--selected' : '',
		checked ? 'item-card--checked' : '',
		selectionActive || selectionMode ? 'item-card--selecting' : '',
		interactive ? 'item-card--interactive' : '',
		expanded ? 'item-card--expanded' : '',
		open ? 'item-card--open' : '',
	].filter(Boolean).join(' ');

	let body = null;
	if (display.mode === 'text' && display.field) {
		const raw = item?.elements?.[display.field.name];
		// A masked long text as the tile's paragraph (KPR-1348): its mask,
		// with Show and Copy, never the words.
		body = isMaskedElement(display.field)
			? <div className="item-card__body"><MaskedValue element={display.field} value={raw} resetKey={item?._id} /></div>
			: display.field.dataType === 'rich-text'
			? (
				<MarkdownContent
					markdown={(raw || '').toString()}
					className="item-card__body item-card__body--clamp"
				/>
			)
			: (
				<div className="item-card__body item-card__body--clamp">
					{(raw || '').toString() || '—'}
				</div>
			);
	} else if (display.mode === 'rows') {
		// An arranged body: rows of fields, each field taking its share of the
		// tile's three columns. Every field reads label over value, whatever the
		// number of rows — a label set beside its value cut both to nothing. Two
		// or three such rows need a taller frame, which the GRID sets for every
		// tile it holds (`tileFrame`), so the tiles of a list stay one height.
		// A file in a body of several rows is the dense chip: a 72px photo is a
		// row of its own.
		const dense = display.rows.length > 1;
		body = display.rows.length ? (
			<div className="item-card__rows">
				{display.rows.map((row, index) => (
					<div className="item-card__fields" key={`row-${index}`}>
						{row.map(({ field, span }) => (
							<CardField
								key={field.name}
								element={field}
								value={item?.elements?.[field.name]}
								endValue={field.rangeEndDef ? item?.elements?.[field.rangeEndDef.name] : undefined}
								resolveLookupValue={resolveLookupValue}
								unitSystem={unitSystem}
								collectionId={collectionId}
								span={span}
								item={item}
								dense={dense}
							/>
						))}
					</div>
				))}
			</div>
		) : null;
	} else if (display.fields.length) {
		body = (
			<div className="item-card__fields">
				{display.fields.map(def => (
					<CardField
						key={def.name}
						element={def}
						value={item?.elements?.[def.name]}
						endValue={def.rangeEndDef ? item?.elements?.[def.rangeEndDef.name] : undefined}
						resolveLookupValue={resolveLookupValue}
						unitSystem={unitSystem}
						collectionId={collectionId}
						item={item}
					/>
				))}
			</div>
		);
	}

	return (
		<div
			className={classes}
			style={accentStyle}
			data-item-id={normalizeId(item?._id)}
			aria-selected={interactive ? selected : undefined}
			{...interactiveProps}
		>
			<div className="item-card__header">
				{/* Identity: the item's two-line badge — the card's 36px chip,
				    the card name muted over the item's title (plus the 3px
				    edge). Plain, not a pill: the card is the item's own
				    surface, not a reference to it. */}
				{/* The full title on hover and, from the keyboard, when the tile
				    itself takes focus (KPR-1231): the tile is the one focus
				    stop, so the tooltip is held open by its focus. The words
				    stay whole in the DOM, so the accessible name is the title. */}
				<Tooltip
					title={title}
					open={titleTipFocus || (titleTipHover && !overQualifier)}
					onOpenChange={setTitleTipHover}
				>
					<span
						className="item-card__identity-slot"
						onMouseOver={event => setOverQualifier(Boolean(event.target.closest?.('[data-testid="record-qualifier"]')))}
						onMouseLeave={() => setOverQualifier(false)}
					>
						<RecordTitle
							kind="item"
							record={card}
							kicker={cardType}
							title={title}
							size="md"
							className="item-card__identity"
							collection={rowSourceIdentity(rowSource) || ownCollection}
							collectionRelation={rowSource?.via || null}
						/>
					</span>
				</Tooltip>
				{/* The tags and the date. Wider than a phone this wrapper is
				    `display: contents`, so they sit in the header row as ever;
				    on a phone it is the line under the badge, so the title keeps
				    the row (KPR-1231). */}
				<span className="item-card__subline">
					<IntakePendingTag item={item} className="item-card__pending" />
					<DemoTag item={item} className="item-card__pending" />
					<span className="item-card__spacer" />
					{headerDate ? (
						<Tooltip title={`${resolved.label} · ${headerDateFull}`}>
							<span className="item-card__date" data-testid="item-card-date">{headerDate}</span>
						</Tooltip>
					) : null}
				</span>
				{!readOnly ? (
					// The action cluster must never bubble a click into "select".
					<span
						className="item-card__actions"
						onClick={event => event.stopPropagation()}
						onKeyDown={event => event.stopPropagation()}
						role="presentation"
					>
						{/* Open · ⋯ · chevron · checkbox, all at the right so the
						    card's chip leads the row. Open, ⋯ and the checkbox take
						    no room at rest — the title has it — and arrive on
						    hover or focus; the checkbox stays on every tile once
						    anything is checked (item-cards.css). It is last so
						    the buttons arriving on hover never move it. */}
						<ItemOpenButton item={item} collectionId={collectionId} className="item-card__open" />
						<ActionsMenu
							{...(actionsMenuProps || {})}
							surface="card"
							item={item}
							card={card}
							elementDefs={elementDefs}
							resolveLookupValue={resolveLookupValue}
							collectionId={collectionId}
							collection={collection}
							canEdit={canEdit}
							canManage={canManageRecord}
							cardsById={cardsById}
							exclude={isLinkedRow(item) ? LINKED_ROW_EXCLUDED_ACTIONS : undefined}
							onDeleted={onDeleted}
							onItemUpdated={onItemUpdated}
							className="item-card__kebab"
							buttonProps={{ type: 'text', size: 'small' }}
						/>
						{inlineExpandable ? (
							<Button
								type="text"
								size="small"
								className="item-card__chevron"
								icon={<ChevronIcon />}
								aria-label={expanded ? 'Hide details' : 'Show details'}
								aria-expanded={expanded}
								onClick={toggleExpanded}
							/>
						) : null}
						{selectable ? (
							<Tooltip title={checkable ? undefined : checkDisabledReason}>
								<span className="item-card__check">
									<Checkbox
										checked={checked}
										disabled={!checkable}
										onClick={event => toggleCheck(event)}
										onChange={() => {}}
										aria-label={`Select ${title || 'item'}`}
									/>
								</span>
							</Tooltip>
						) : null}
					</span>
				) : null}
			</div>

			<div className="item-card__compact">
				<div>
					{/* The body takes what is left and clips; the audit footer
					    keeps its own row, so the card never cuts its last line. */}
					<div className="item-card__body-slot">{body}</div>
					{tagEntries.length ? (
						<ItemTagChips entries={tagEntries} max={3} singleLine className="item-card__tags" cardsById={tagContext?.public ? tagContext.cardsById : null} />
					) : null}
					{/* In a combined list a copy names its original's collection (KPR-1024). */}
					{ownCollection ? <CopiedFromNote item={item} compact className="item-card__copied" /> : null}
					<ItemAuditLine item={item} className="item-card__meta" />
				</div>
			</div>

			{typeof renderExpanded === 'function' ? (
				<div className="item-card__expand" aria-hidden={!expanded}>
					<div>{expanded ? renderExpanded(item) : null}</div>
				</div>
			) : null}
		</div>
	);
};

export const ItemCard = memo(ItemCardTile);
